import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Personal Creator Studio aggregates — REAL METRICS (Phase 4).
 * - Nation source of truth: public.profiles.selection_team (same as Nation Wars).
 * - Shares  = victory_card_shares (by user_id) + audit_master share-* events
 *             attributed to user_id OR event_data->>'ref_code'.
 * - Clicks  = audit_master *_click* events (incl. referral_link_clicked)
 *             attributed to user_id OR ref_code.
 * - Views   = audit_master referral_landing_view / nation_wars_share_landing_view
 *             attributed to user_id OR ref_code.
 * - Score   = shares*3 + clicks*2 + views (real).
 * Clip list / platform / nation breakdown still come from viral_clips so the
 * existing "Mis Clips Virales" surface keeps working.
 */

export interface CreatorStudioSnapshot {
  nation: string | null;
  totals: { clips: number; views: number; shares: number; clicks: number };
  byPlatform: Array<{ platform: string; clips: number; shares: number }>;
  byNation: Array<{ nation: string; clips: number }>;
  recent: Array<{
    id: string;
    kind: string;
    nation: string | null;
    rival: string | null;
    headline: string | null;
    minute: string | null;
    platform: string | null;
    views: number;
    shares: number;
    clicks: number;
    created_at: string;
  }>;
  rank: { position: number | null; total: number; score: number };
}

interface SnapshotInput { ref_code?: string | null }

export const getCreatorStudioSnapshot = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: SnapshotInput | undefined) => ({
    ref_code:
      typeof d?.ref_code === "string" && /^[A-Z0-9]{4,32}$/.test(d.ref_code)
        ? d.ref_code
        : (null as string | null),
  }))
  .handler(async ({ context, data }): Promise<CreatorStudioSnapshot> => {
    const { supabase, userId } = context;
    const refCode = data.ref_code;

    // 0) Single source of truth for nation — same field Nation Wars reads.
    const { data: prof } = await supabase
      .from("profiles")
      .select("selection_team")
      .eq("id", userId)
      .maybeSingle();
    const nation = (prof?.selection_team as string | null) ?? null;

    // 1) My clips (all time, recent first) — used for clip list + breakdowns only.
    const { data: mine } = await supabase
      .from("viral_clips")
      .select("id, home_team, away_team, score, minute, emotion, platform, views, shares, clicks, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(200);

    const rows = mine ?? [];
    const clipsCount = rows.length;

    const platformMap = new Map<string, { clips: number; shares: number }>();
    const nationMap = new Map<string, number>();
    for (const r of rows) {
      const p = r.platform ?? "versesport";
      const cur = platformMap.get(p) ?? { clips: 0, shares: 0 };
      cur.clips += 1;
      cur.shares += r.shares ?? 0;
      platformMap.set(p, cur);
      if (r.home_team) nationMap.set(r.home_team, (nationMap.get(r.home_team) ?? 0) + 1);
    }

    const recent = rows.slice(0, 12).map((r) => ({
      id: r.id as string,
      kind: (r.emotion as string) ?? "mi_clip_viral",
      nation: (r.home_team as string) ?? null,
      rival: (r.away_team as string) ?? null,
      headline: (r.score as string) ?? null,
      minute: (r.minute as string) ?? null,
      platform: (r.platform as string) ?? null,
      views: r.views ?? 0,
      shares: r.shares ?? 0,
      clicks: r.clicks ?? 0,
      created_at: r.created_at as string,
    }));

    // 2) REAL metrics via SECURITY DEFINER RPC (audit_master + victory_card_shares).
    const { data: metricsRow } = await supabase.rpc("creator_real_metrics", {
      p_user: userId,
      p_ref: refCode ?? undefined,
    });
    const m = Array.isArray(metricsRow) ? metricsRow[0] : metricsRow;
    const realShares = Number(m?.shares ?? 0);
    const realClicks = Number(m?.clicks ?? 0);
    const realViews  = Number(m?.views ?? 0);

    const totals = {
      clips: clipsCount,
      views: realViews,
      shares: realShares,
      clicks: realClicks,
    };

    // 3) Global ranking (last 24h) from viral_clips (existing snapshot).
    const since = new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString();
    const { data: all } = await supabase
      .from("viral_clips")
      .select("user_id, views, shares, clicks")
      .gte("created_at", since)
      .limit(2000);
    const agg = new Map<string, number>();
    for (const c of all ?? []) {
      if (!c.user_id) continue;
      const score = (c.shares ?? 0) * 3 + (c.clicks ?? 0) * 2 + (c.views ?? 0);
      agg.set(c.user_id, (agg.get(c.user_id) ?? 0) + score);
    }
    // Inject my real score so position reflects real attribution.
    const myRealScore = realShares * 3 + realClicks * 2 + realViews;
    if (myRealScore > 0) agg.set(userId, Math.max(agg.get(userId) ?? 0, myRealScore));
    const ranked = Array.from(agg.entries()).sort((a, b) => b[1] - a[1]);
    const myIdx = ranked.findIndex(([uid]) => uid === userId);

    return {
      nation,
      totals,
      byPlatform: Array.from(platformMap.entries())
        .map(([platform, v]) => ({ platform, ...v }))
        .sort((a, b) => b.clips - a.clips),
      byNation: Array.from(nationMap.entries())
        .map(([nation, clips]) => ({ nation, clips }))
        .sort((a, b) => b.clips - a.clips)
        .slice(0, 6),
      recent,
      rank: {
        position: myIdx >= 0 ? myIdx + 1 : null,
        total: ranked.length,
        score: myRealScore,
      },
    };
  });